if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        
        // Prevent double submission
        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.submitButton.classList.contains('loading')) {
          return;
        }

        // Validate form before submission
        const variantId = this.form.querySelector('[name=id]')?.value;
        if (!variantId || variantId === '0' || variantId === '') {
          // console.error('[Product Form] Invalid variant ID:', variantId);
          this.handleErrorMessage('Please select a valid product option.');
          return;
        }

        // Validate quantity
        const quantityInput = this.form.querySelector('[name=quantity]');
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
        if (isNaN(quantity) || quantity < 1) {
          // console.error('[Product Form] Invalid quantity:', quantity);
          this.handleErrorMessage('Please enter a valid quantity.');
          return;
        }

        this.handleErrorMessage();
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading-overlay__spinner');
        if (spinner) {
          spinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        
        // Re-check cart element (may not exist at constructor time)
        const cartElement = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        
        if (cartElement && typeof cartElement.getSectionsToRender === 'function') {
          try {
            formData.append(
              'sections',
              cartElement.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            if (typeof cartElement.setActiveElement === 'function') {
              cartElement.setActiveElement(document.activeElement);
            }
          } catch (e) {
            // console.warn('[Product Form] Error setting cart sections:', e);
          }
        }
        
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => {
            // Check if response is ok
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then((response) => {
            // Check for API-level errors
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              
              const errorMessage = response.description || response.message || 'Unable to add product to cart. Please try again.';
              this.handleErrorMessage(errorMessage);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', true);
                const span = this.submitButton.querySelector('span');
                if (span) span.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
              this.error = true;
              return;
            }

            // Verify response has expected structure
            if (!response || (!response.items && !response.item_count)) {
              // console.error('[Product Form] Invalid cart response:', response);
              this.handleErrorMessage('Invalid response from server. Please try again.');
              this.error = true;
              return;
            }

            // Run 3 (revert 2026-09-02) — the router fires post_add_complete;
            // on the REDIRECT arm it takes over the navigation. On the modal
            // arm we fall through UNCHANGED to the original variantaug
            // notification/drawer render below. Loaded on product templates
            // only; elsewhere JRS.PostAdd is absent and the original stays.
            if (window.JRS && window.JRS.PostAdd && typeof window.JRS.PostAdd.complete === 'function' &&
                window.JRS.PostAdd.complete({ path: 'product_form' }) === 'redirect') {
              if (!this.error) {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id') });
              }
              this.error = false;
              return;
            }

            // Re-check cart element before rendering
            const currentCartElement = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
            
            if (!currentCartElement || typeof currentCartElement.renderContents !== 'function') {
              // Fallback: redirect to cart page
              if (window.routes && window.routes.cart_url) {
                window.location.href = window.routes.cart_url;
              } else {
                window.location.href = '/cart';
              }
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id') });
            }
            
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    try {
                      currentCartElement.renderContents(response);
                    } catch (e) {
                      // console.error('[Product Form] Error rendering cart contents:', e);
                      window.location.href = window.routes?.cart_url || '/cart';
                    }
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              try {
                currentCartElement.renderContents(response);
              } catch (e) {
                // console.error('[Product Form] Error rendering cart contents:', e);
                window.location.href = window.routes?.cart_url || '/cart';
              }
            }
          })
          .catch((e) => {
            // console.error('[Product Form] Error adding to cart:', e);
            this.error = true;
            
            // Provide user-friendly error message
            let errorMessage = 'Unable to add product to cart. Please check your connection and try again.';
            
            if (e.message && e.message.includes('Failed to fetch')) {
              errorMessage = 'Network error. Please check your internet connection and try again.';
            } else if (e.message && e.message.includes('HTTP error')) {
              errorMessage = 'Server error. Please try again in a moment.';
            }
            
            this.handleErrorMessage(errorMessage);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            const finalCartElement = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
            if (finalCartElement && finalCartElement.classList.contains('is-empty')) {
              finalCartElement.classList.remove('is-empty');
            }
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }
            const spinner = this.querySelector('.loading-overlay__spinner');
            if (spinner) {
              spinner.classList.add('hidden');
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
